import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Customer, Dashboard, Target } from '../types/models';

interface CustomerState {
  contactName: string;
  customerName: string | null;
  customers: Customer[];
  dashboards: Dashboard[];
  disabled: boolean;
  email: string;
  error: boolean;
  errorMessage: string;
  isCustomersLoading: boolean;
  isCustomerDeleting: boolean;
  isCustomerValid: boolean;
  isDashboardsLoading: boolean;
  isTargetsLoading: boolean;
  isUsersLoading: boolean;
  targets: Target[];
  // Written by some reducers but not part of the seed state:
  isCustomerLoading?: boolean;
  isDashboardSaving?: boolean;
}

const initialState: CustomerState = {
  contactName: '',
  customerName: null,
  customers: [],
  dashboards: [],
  disabled: false,
  email: '',
  error: false,
  errorMessage: '',
  isCustomersLoading: false,
  isCustomerDeleting: false,
  isCustomerValid: false,
  isDashboardsLoading: false,
  isTargetsLoading: false,
  isUsersLoading: false,
  targets: [],
};

// M6: RTK createSlice. Reducers keep their plain-object bodies; sync action creators
// generated from the keys. The targets/dashboards/customers fetch thunks dispatch these.
const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    requestUserTargets(state) {
      return { ...state, isTargetsLoading: true, error: false, errorMessage: '' };
    },
    requestUserTargetsError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isTargetsLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_target_error_' };
    },
    requestUserTargetsSuccess(state, action: PayloadAction<{ targets: Target[] }>) {
      return { ...state, isTargetsLoading: false, error: false, errorMessage: '', targets: action.payload.targets };
    },
    requestUserTargetsRoot(state) {
      return { ...state, isTargetsLoading: true, error: false, errorMessage: '' };
    },
    requestUserTargetsRootError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isTargetsLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_target_error_' };
    },
    requestUserTargetsRootSuccess(state, action: PayloadAction<{ targets: Target[] }>) {
      return { ...state, isTargetsLoading: false, error: false, errorMessage: '', targets: action.payload.targets };
    },
    requestUserDashboards(state) {
      return { ...state, isDashboardsLoading: true, error: false, errorMessage: '' };
    },
    requestUserDashboardsError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isDashboardsLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_dashboard_error_' };
    },
    requestUserDashboardsSuccess(state, action: PayloadAction<Dashboard[]>) {
      return { ...state, isDashboardsLoading: false, error: false, errorMessage: '', dashboards: action.payload };
    },
    requestUserCustomers(state) {
      return { ...state, isCustomersLoading: true, error: false, errorMessage: '' };
    },
    requestUserCustomersError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isCustomersLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_companies_error_' };
    },
    requestUserCustomersSuccess(state, action: PayloadAction<Customer[]>) {
      return { ...state, isCustomersLoading: false, error: false, errorMessage: '', customers: action.payload };
    },
    requestUserCustomer(state) {
      return { ...state, isCustomersLoading: true, error: false, errorMessage: '' };
    },
    requestUserCustomerError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isCustomersLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_companies_error_' };
    },
    requestUserCustomerSuccess(state, action: PayloadAction<Customer[]>) {
      return { ...state, isCustomersLoading: false, error: false, errorMessage: '', customers: action.payload };
    },
    // Writes an arbitrary form field by name; cast for the same reason as editUser.
    requestEditUserCustomer(state, action: PayloadAction<{ property: string; value: unknown }>) {
      return { ...state, [action.payload.property]: action.payload.value } as CustomerState;
    },
    requestSaveUserCustomer(state) {
      return { ...state, isCustomerLoading: false, error: false, errorMessage: '' };
    },
    requestSaveUserCustomerSuccess(state) {
      return { ...state, isCustomersLoading: false, error: false, errorMessage: '' };
    },
    requestSaveUserCustomerError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isCustomersLoading: false, error: true, errorMessage: action.payload.errorMessage || '_empty_customer_error_' };
    },
    requestDeleteCustomer(state) {
      return { ...state, isCustomerDeleting: true, error: false, errorMessage: '' };
    },
    requestDeleteCustomerSuccess(state) {
      return { ...state, isCustomerDeleting: false, error: false, errorMessage: '' };
    },
    requestDeleteCustomerError(state, action: PayloadAction<{ errorMessage?: string }>) {
      return { ...state, isCustomerDeleting: false, error: true, errorMessage: action.payload.errorMessage || '_empty_customer_error_' };
    },
    requestValidateUserCustomer(state, action: PayloadAction<boolean>) {
      return { ...state, isCustomersLoading: false, error: false, errorMessage: '', isCustomerValid: action.payload };
    },
    resetCustomer() {
      return initialState;
    },
  },
});

export const {
  requestUserTargets, requestUserTargetsError, requestUserTargetsSuccess,
  requestUserTargetsRoot, requestUserTargetsRootError, requestUserTargetsRootSuccess,
  requestUserDashboards, requestUserDashboardsError, requestUserDashboardsSuccess,
  requestUserCustomers, requestUserCustomersError, requestUserCustomersSuccess,
  requestUserCustomer, requestUserCustomerError, requestUserCustomerSuccess,
  requestEditUserCustomer,
  requestSaveUserCustomer, requestSaveUserCustomerSuccess, requestSaveUserCustomerError,
  requestDeleteCustomer, requestDeleteCustomerSuccess, requestDeleteCustomerError,
  requestValidateUserCustomer,
  resetCustomer,
} = customerSlice.actions;
export default customerSlice.reducer;
